import type { Feature } from '@app/hooks/useFeatureState';
import { getIsFeatureEnabled, getTaskCountFromTasks } from '@app/selectors';
import type { RootState } from '@app/types';
import track, { AnalyticsEventName } from '@app/utils/analytics';

import type { NavigationItem } from '@app/features/project/types/navigation';
import { ProjectType } from '@app/features/project/types/project';
import getProjectNavigationVisibilityMap from './toolbarVisibility';

const getTaskCount = (
    project: RootState['project'],
    branch: RootState['branch'],
    taskBadges: RootState['badges']['tasks'],
) => getTaskCountFromTasks(taskBadges || undefined, project.id, branch.branchId || undefined);

const getProjectNavigationMap = ({
    project,
    userRights,
    session: { planId },
    branch,
    badges,
    features,
    marketingSupportProjectSelectedAppSlug,
}: {
    project: RootState['project'];
    userRights: RootState['userRights'];
    session: RootState['session'];
    branch: RootState['branch'];
    badges: RootState['badges'];
    features: RootState['features'];
    marketingSupportProjectSelectedAppSlug: string;
}): NavigationItem[] => {
    const taskCount = getTaskCount(project, branch, badges.tasks);

    const isFeatureEnabled = (feature: Feature) => getIsFeatureEnabled(features, feature);

    const navigationVisibility = getProjectNavigationVisibilityMap(project, isFeatureEnabled);

    const appsNavigationItem = {
        key: 'apps',
        label: 'Apps',
        isActive: (input: string) => input === 'apps',
        link: `/apps/${project.id}/`,
        ...navigationVisibility.apps,
    };

    const appsNavigationItemArrInMainMenu = [];
    const appsNavigationItemArrInMoreMenu = [];
    if (project.type === ProjectType.contentIntegration) {
        appsNavigationItemArrInMoreMenu.push(appsNavigationItem);
    } else {
        appsNavigationItemArrInMainMenu.push(appsNavigationItem);
    }

    return [
        {
            key: 'edit',
            label: 'Editor',
            link: `/project/${project.id}/`,
            isActive: (input: string) => input === 'edit',
            ...navigationVisibility.editor,
        },
        {
            key: 'files',
            label: 'Files',
            link: `/files/${project.id}/`,
            isActive: (input: string) => input === 'files',
            ...navigationVisibility.files,
        },
        ...(project.type === ProjectType.contentIntegration
            ? [
                  {
                      key: 'contentManagement',
                      label: 'Content management',
                      isActive: (input: string) => input === 'apps',
                      link: `/apps/${project.id}/${marketingSupportProjectSelectedAppSlug}`,
                      ...navigationVisibility.contentManagement,
                  },
              ]
            : [
                  {
                      key: 'upload',
                      label: 'Upload',
                      link: `/upload/${project.id}/`,
                      isActive: (input: string) => input === 'upload',
                      ...navigationVisibility.upload,
                  },
                  {
                      key: 'download',
                      label: 'Download',
                      link: `/download/${project.id}/`,
                      isActive: (input: string) => input === 'download',
                      ...navigationVisibility.download,
                  },
              ]),
        {
            key: 'tasks',
            label: 'Tasks',
            link: `/tasks/${project.id}/`,
            isActive: (input: string) => input === 'tasks',
            badge: taskCount,
            ...navigationVisibility.tasks,
        },
        {
            key: 'workflows',
            label: 'Workflows',
            link: `/workflows/${project.id}/`,
            isActive: (input: string) => input === 'workflows',
            novel: 'New',
            onClick: () => {
                track(AnalyticsEventName.EDITOR_WORKFLOWS_TAB_CLICKED);

                window.location.assign(`/workflows/${project.id}/`);
            },
            ...navigationVisibility.workflows,
        },
        {
            key: 'contributors',
            label: 'Contributors',
            link: `/contributors/${project.id}/`,
            isActive: (input: string) => input === 'contributors',
            ...navigationVisibility.contributors,
        },
        {
            key: 'screenshots',
            label: 'Screenshots',
            link: `/screenshots/${project.id}/`,
            isActive: (input: string) => input === 'screenshots',
            ...navigationVisibility.screenshots,
        },
        {
            key: 'glossary',
            label: 'Glossary',
            link: `/glossary/${project.id}/`,
            isActive: (input: string) => input === 'glossary',
            ...navigationVisibility.glossary,
        },
        ...appsNavigationItemArrInMainMenu,
        {
            key: 'more',
            label: 'More...',
            isActive: (input: string) => ['settings', 'statistics', 'duplicates', 'live edit'].includes(input),
            ...navigationVisibility.more,
            dropdown: [
                [
                    {
                        link: `/settings/${project.id}/`,
                        label: 'Settings',
                        ...navigationVisibility.settings,
                    },
                    {
                        link: `/settings/${project.id}/#branches`,
                        label: 'Branches',
                        ...navigationVisibility.branches,
                    },
                    {
                        link: `/settings/${project.id}/#ota-bundles`,
                        label: 'OTA Bundles',
                        ...navigationVisibility.otaBundles,
                    },
                    {
                        link: `/settings/${project.id}/#project-automations`,
                        label: 'Automations',
                        ...navigationVisibility.automations,
                    },
                    {
                        link: `/dupes/${project.id}/`,
                        label: 'Duplicate finder',
                        ...navigationVisibility.duplicateFinder,
                    },
                ],
                [
                    ...appsNavigationItemArrInMoreMenu,
                    {
                        label: 'Activity',
                        onClick: () => {
                            const showActivityEvent = new CustomEvent('show-activity', {
                                detail: {
                                    projectId: project.id,
                                    branchId: branch.branchId,
                                    branchName: branch.branchName,
                                },
                            });
                            document.dispatchEvent(showActivityEvent);
                        },
                        ...navigationVisibility.activity,
                    },
                    {
                        link: `/statistics/${project.id}/`,
                        label: 'Statistics',
                        ...navigationVisibility.statistics,
                    },
                ],
            ],
        },
    ];
};

export default getProjectNavigationMap;
